// vim: set ts=2 sts=2 sw=2 et:

import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";

import type { GlobalState } from "../../types/stateTypes";
import type { Dispatch } from "redux";
import { fetchWrpfBundle } from "./fetchWrpfBundle";
import { sessionFromWrpfHandoff, setWrpfSession, updateWrpfToken } from "./wrpfSessionActions";
import type { OpenlifterBundleFile, WrpfBundleMeta } from "./types";
import { applyWrpfBundle } from "./applyWrpfBundle";
import { bundleHasMeet } from "./buildExportBundle";
import { overwriteStore } from "../../actions/globalActions";
import { normalizeGlobalStateDivisions } from "./normalizeWrpfDivisions";
import { remapEntryAnchors } from "./remapEntryAnchors";
import { refreshWrpfRecords } from "../records/wrpfRecordsReducer";
import { shouldConfirmTournamentSwitch } from "./shouldConfirmTournamentSwitch";
import WrpfSwitchTournamentModal from "./WrpfSwitchTournamentModal";
import { wrpfHandoffDebug, wrpfHandoffError } from "./wrpfHandoffDebug";

/** Lee (sin verificar) los claims del JWT para reconocer un torneo local durante un fallback offline. */
function decodeTokenClaims(token: string): { sub?: string; exp?: number } | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as { sub?: string; exp?: number };
  } catch {
    return null;
  }
}

type StateProps = {
  state: GlobalState;
};

type DispatchProps = {
  applyBundle: (bundle: OpenlifterBundleFile, wrpf: WrpfBundleMeta) => void;
  refreshRecords: (wrpf: WrpfBundleMeta) => void;
  setWrpfSession: (session: GlobalState["wrpfSession"]) => void;
  refreshToken: (params: { callbackToken: string; callbackUrl: string; tokenExpiresAt: string }) => void;
  overwriteStoreState: (state: GlobalState) => void;
};

type Props = StateProps & DispatchProps;

type PendingSwitch = {
  bundle: OpenlifterBundleFile;
  wrpf: WrpfBundleMeta;
  currentMeetName: string;
};

const WrpfStartInner = ({
  state,
  applyBundle,
  refreshRecords,
  setWrpfSession: setSession,
  refreshToken,
  overwriteStoreState,
}: Props) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const finishToRegistration = useCallback(() => {
    navigate("/registration", { replace: true });
  }, [navigate]);

  const handleConfirmSwitch = useCallback(() => {
    if (!pendingSwitch) return;
    const bundleUrl = params.get("u");
    const token = params.get("t");
    applyBundle(pendingSwitch.bundle, pendingSwitch.wrpf);
    if (bundleUrl && token) {
      setSession(sessionFromWrpfHandoff(pendingSwitch.wrpf, bundleUrl, token));
    }
    setPendingSwitch(null);
    finishToRegistration();
  }, [applyBundle, finishToRegistration, pendingSwitch, params, setSession]);

  const handleCancelSwitch = useCallback(() => {
    setPendingSwitch(null);
    setLoading(false);
    finishToRegistration();
  }, [finishToRegistration]);

  useEffect(() => {
    const bundleUrl = params.get("u");
    const token = params.get("t");
    const tokenClaims = token ? decodeTokenClaims(token) : null;

    wrpfHandoffDebug("WrpfStart mount", {
      pathname: window.location.pathname,
      bundleUrl,
      hasToken: Boolean(token),
    });

    if (!bundleUrl || !token) {
      wrpfHandoffError("missing query params", { bundleUrl, tokenPresent: Boolean(token) });
      setError("Faltan parámetros u (bundle URL) o t (token JWT).");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPendingSwitch(null);

    (async () => {
      try {
        const bundle = await fetchWrpfBundle(bundleUrl, token);
        if (cancelled) return;

        const wrpf = bundle._wrpf;
        if (!wrpf || wrpf.schemaVersion !== 1) {
          throw new Error("Bundle WRPF inválido (_wrpf.schemaVersion)");
        }

        const currentState = stateRef.current;
        const sameTournamentLoaded =
          bundleHasMeet(currentState) && currentState.wrpfSession.tournamentId === wrpf.tournamentId;

        if (sameTournamentLoaded && wrpf.callback) {
          // El meet local se preserva, pero las divisiones legacy (bandas Master de 4-5 años de
          // bundles pre-reglamento-ARG que sobreviven en localStorage) se normalizan al vigente.
          const normalized = normalizeGlobalStateDivisions(currentState);
          if (normalized) {
            wrpfHandoffDebug("normalizing legacy division strings to WRPF Argentina rulebook");
            overwriteStoreState(normalized);
          }
          refreshRecords(wrpf);
          const localState = normalized ?? currentState;
          setSession({
            ...sessionFromWrpfHandoff(wrpf, bundleUrl, token),
            entryAnchors: remapEntryAnchors({
              localEntries: localState.registration.entries,
              localAnchors: localState.wrpfSession.entryAnchors,
              incoming: bundle,
            }),
          });
          finishToRegistration();
          return;
        }

        if (shouldConfirmTournamentSwitch(currentState, wrpf.tournamentId)) {
          setPendingSwitch({
            bundle,
            wrpf,
            currentMeetName: currentState.meet.name,
          });
          setLoading(false);
          return;
        }

        applyBundle(bundle, wrpf);
        setSession(sessionFromWrpfHandoff(wrpf, bundleUrl, token));
        wrpfHandoffDebug("handoff complete, navigating to /registration");
        finishToRegistration();
      } catch (err) {
        if (!cancelled) {
          const currentState = stateRef.current;
          if (
            tokenClaims?.sub &&
            bundleHasMeet(currentState) &&
            currentState.wrpfSession.tournamentId === tokenClaims.sub &&
            currentState.wrpfSession.callbackUrl
          ) {
            wrpfHandoffError("record metadata refresh failed — preserving the already loaded tournament", err);
            refreshToken({
              callbackToken: token,
              callbackUrl: currentState.wrpfSession.callbackUrl,
              tokenExpiresAt: tokenClaims.exp
                ? new Date(tokenClaims.exp * 1000).toISOString()
                : currentState.wrpfSession.tokenExpiresAt ?? "",
            });
            setLoading(false);
            finishToRegistration();
            return;
          }
          wrpfHandoffError("handoff failed", err);
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    params,
    applyBundle,
    refreshRecords,
    setSession,
    refreshToken,
    overwriteStoreState,
    finishToRegistration,
  ]);

  if (pendingSwitch) {
    return (
      <>
        <WrpfSwitchTournamentModal
          show
          currentMeetName={pendingSwitch.currentMeetName}
          newMeetName={pendingSwitch.wrpf.tournamentName}
          onCancel={handleCancelSwitch}
          onConfirm={handleConfirmSwitch}
        />
        <div className="text-center p-5 text-muted">
          <p>Esperando confirmación para cargar {pendingSwitch.wrpf.tournamentName}…</p>
        </div>
      </>
    );
  }

  if (loading && !error) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Cargando torneo desde WRPF…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        <Alert.Heading>No se pudo iniciar desde WRPF</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return null;
};

const mapState = (state: GlobalState): StateProps => ({ state });

const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  applyBundle: (bundle, wrpf) => applyWrpfBundle(dispatch, bundle, wrpf),
  refreshRecords: (wrpf) =>
    dispatch(
      refreshWrpfRecords({
        tournamentScope: wrpf.tournamentScope ?? null,
        recordCategories: wrpf.recordCategories ?? [],
        currentRecords: wrpf.currentRecords ?? [],
      }),
    ),
  setWrpfSession: (session) => dispatch(setWrpfSession(session)),
  refreshToken: (params) => dispatch(updateWrpfToken(params)),
  overwriteStoreState: (state) => dispatch(overwriteStore(state)),
});

const WrpfStartContainer = connect(mapState, mapDispatch)(WrpfStartInner);

export default WrpfStartContainer;
